import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { apiGet } from "../services/restApiService";
import { useAuth } from "../supabase/AuthProvider";
import { Feature as SharedFeature } from "@pedagogue/entitlements-types";
import type { PlanTier } from "@pedagogue/entitlements-types";

export const Feature = SharedFeature;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type Feature = `${SharedFeature}`;

/**
 * EntitlementsContext — Phase 3b of the B2B 14-day Trial go-live
 * (plan §D.2). Single fetch of the backend /users/me/entitlements
 * response on auth change + on window focus / visibilitychange so
 * UI gates update across tab-inactive day-14 expiries without a
 * hard refresh.
 *
 * Consumers use `hasFeature(Feature.BULK_INVITE)` etc. to gate UI
 * surfaces. The backend has already narrowed PLAN_FEATURES for
 * trial orgs via the `features` override dict (see Phase 2e); the
 * frontend merely reads the server-derived list.
 */

export interface EntitlementsData {
  planTier: PlanTier;
  subscriptionValidUntil: string | null;
  features: Feature[];
  featureOverrides: Record<string, boolean>;
  isSubscriptionExpired: boolean;
  isTrialOrg: boolean;
  packageSku: string | null;
}

interface EntitlementsContextType {
  entitlements: EntitlementsData | null;
  loading: boolean;
  error: string | null;
  hasFeature: (feature: Feature) => boolean;
  refresh: () => Promise<void>;
}

const EntitlementsContext = createContext<EntitlementsContextType | undefined>(
  undefined,
);

export const useEntitlements = (): EntitlementsContextType => {
  const ctx = useContext(EntitlementsContext);
  if (!ctx) {
    throw new Error(
      "useEntitlements must be used within an EntitlementsProvider",
    );
  }
  return ctx;
};

interface EntitlementsProviderProps {
  children: ReactNode;
}

export const EntitlementsProvider: React.FC<EntitlementsProviderProps> = ({
  children,
}) => {
  const { user } = useAuth();
  const [entitlements, setEntitlements] = useState<EntitlementsData | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  const fetchEntitlements = useCallback(async () => {
    if (fetchingRef.current) return;
    if (!user) {
      setEntitlements(null);
      setError(null);
      return;
    }
    fetchingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet<EntitlementsData>("/users/me/entitlements");
      setEntitlements(data);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to fetch entitlements";
      setError(message);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [user]);

  useEffect(() => {
    if (user?.id) {
      void fetchEntitlements();
    } else if (!user) {
      setEntitlements(null);
      setError(null);
    }
  }, [user, fetchEntitlements]);

  // Re-fetch on window focus / visibilitychange so day-14 trial
  // expiry surfaces without a manual refresh. Matches plan §D.3
  // intent + keeps the UI gate responsive on long tab-inactive
  // sessions.
  useEffect(() => {
    if (!user?.id) return;
    const onActivate = () => {
      if (document.visibilityState === "visible") {
        void fetchEntitlements();
      }
    };
    window.addEventListener("focus", onActivate);
    document.addEventListener("visibilitychange", onActivate);
    return () => {
      window.removeEventListener("focus", onActivate);
      document.removeEventListener("visibilitychange", onActivate);
    };
  }, [user?.id, fetchEntitlements]);

  const hasFeature = useCallback(
    (feature: Feature): boolean => {
      if (!entitlements) return false;
      if (entitlements.isSubscriptionExpired) return false;
      return entitlements.features.includes(feature);
    },
    [entitlements],
  );

  return (
    <EntitlementsContext.Provider
      value={{
        entitlements,
        loading,
        error,
        hasFeature,
        refresh: fetchEntitlements,
      }}
    >
      {children}
    </EntitlementsContext.Provider>
  );
};
